import { useEffect, useState } from "react";
import { useApiFetch } from "../../api/client";
import { useIntakeCounts } from "../../app/IntakeCountsContext";

interface RefCode {
  code: string;
  displayName: string;
}

interface ResinType {
  resinTypeCode: string;
  displayName: string;
}

interface ResinFamily {
  resinFamilyCode: string;
  displayName: string;
  types: ResinType[];
}

interface Supplier {
  supplierId: number;
  supplierName: string;
  isProducer: boolean;
}

interface SpotNomination {
  spotNominationId: number;
  quotedAtLocal: string;
  supplierName: string;
  resinFamilyCode: string;
  resinTypeCode: string | null;
  materialConditionCode: string | null;
  priceCentsPerLb: number;
  priceBasisCode: string | null;
  packageCode: string | null;
  regionCode: string | null;
  volumeLb: number | null;
  firmnessCode: string;
  sourceChannelCode: string | null;
  notes: string | null;
  status: "Active" | "Voided";
}

function nowLocal(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function localOffsetMinutes(): number {
  return -new Date().getTimezoneOffset();
}

function stampLabel(v: string): string {
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export function SpotLane() {
  const apiFetch = useApiFetch();

  const [firmness, setFirmness] = useState<RefCode[]>([]);
  const [sourceChannels, setSourceChannels] = useState<RefCode[]>([]);
  const [conditions, setConditions] = useState<RefCode[]>([]);
  const [priceBases, setPriceBases] = useState<RefCode[]>([]);
  const [packages, setPackages] = useState<RefCode[]>([]);
  const [regions, setRegions] = useState<RefCode[]>([]);
  const [families, setFamilies] = useState<ResinFamily[]>([]);
  const [producers, setProducers] = useState<Supplier[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [when, setWhen] = useState(nowLocal);
  const [supplierId, setSupplierId] = useState("");
  const [supplierOther, setSupplierOther] = useState("");
  const [source, setSource] = useState("");
  const [family, setFamily] = useState("");
  const [type, setType] = useState("");
  const [cond, setCond] = useState("");
  const [price, setPrice] = useState("");
  const [basis, setBasis] = useState("");
  const [pack, setPack] = useState("");
  const [region, setRegion] = useState("");
  const [qty, setQty] = useState("");
  const [firm, setFirm] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formMsg, setFormMsg] = useState<string | null>(null);

  const [log, setLog] = useState<SpotNomination[]>([]);

  const { setCounts } = useIntakeCounts();

  const refreshLog = async () => {
    const res = await apiFetch("spot?enteredByMe=true&limit=100");
    const rows: SpotNomination[] = await res.json();
    setLog(rows.filter((r) => r.status === "Active"));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [firmRes, srcRes, condRes, basisRes, packRes, regionRes, taxRes, supRes] = await Promise.all([
          apiFetch("reference/Firmness"),
          apiFetch("reference/SourceChannel"),
          apiFetch("reference/MaterialCondition"),
          apiFetch("reference/PriceBasis"),
          apiFetch("reference/Package"),
          apiFetch("reference/Region"),
          apiFetch("reference/resin-taxonomy"),
          apiFetch("suppliers?producersOnly=true&limit=100"),
        ]);
        if (cancelled) return;
        const [firmData, srcData, condData, basisData, packData, regionData, taxData, supData] = await Promise.all([
          firmRes.json(), srcRes.json(), condRes.json(), basisRes.json(), packRes.json(), regionRes.json(), taxRes.json(), supRes.json(),
        ]);
        setFirmness(firmData); setSourceChannels(srcData); setConditions(condData);
        setPriceBases(basisData); setPackages(packData); setRegions(regionData);
        setFamilies(taxData); setProducers(supData);
        setFirm(firmData[0]?.code ?? "");
        setSource(srcData[0]?.code ?? "");
        setCond(condData[0]?.code ?? "");
        setBasis(basisData[0]?.code ?? "");
        setPack(packData[0]?.code ?? "");
        setRegion(regionData[0]?.code ?? "");
        await refreshLog();
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setCounts({ spotQuotesLogged: log.length }); }, [log, setCounts]);

  const supplierName = supplierId === "__other" ? supplierOther.trim() : producers.find((p) => String(p.supplierId) === supplierId)?.supplierName ?? "";
  const ready = !!supplierName && !!family && !!price && !!when && !!firm;
  const selectedFamily = families.find((f) => f.resinFamilyCode === family);

  const logQuote = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    setFormMsg(null);
    try {
      await apiFetch("spot", {
        method: "POST",
        body: JSON.stringify({
          quotedAtLocal: when,
          quotedAtOffsetMinutes: localOffsetMinutes(),
          supplierId: supplierId === "__other" || !supplierId ? null : Number(supplierId),
          supplierNameOther: supplierId === "__other" ? supplierOther.trim() : null,
          sourceChannelCode: source || null,
          resinFamilyCode: family,
          resinTypeCode: type || null,
          materialConditionCode: cond || null,
          priceCentsPerLb: parseFloat(price),
          currencyCode: "USD",
          priceBasisCode: basis || null,
          packageCode: pack || null,
          regionCode: region || null,
          volumeLb: qty ? Number(qty) : null,
          firmnessCode: firm,
          notes: notes.trim() || null,
          idempotencyKey: crypto.randomUUID(),
        }),
      });
      await refreshLog();
      setPrice(""); setQty(""); setNotes("");
      setWhen(nowLocal());
      setFormMsg("Logged. Supplier and resin are kept so you can enter the next level from the same call.");
    } catch (err) {
      setFormMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setPrice(""); setQty(""); setNotes(""); setSupplierId(""); setSupplierOther("");
    setFamily(""); setType(""); setWhen(nowLocal()); setFormMsg(null);
  };

  if (loading) {
    return (
      <section className="lane on">
        <h2>Log a Spot Nomination</h2>
        <div className="empty"><span className="material-symbols-sharp spin">progress_activity</span>Loading reference data…</div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="lane on">
        <h2>Log a Spot Nomination</h2>
        <div className="empty">
          <span className="material-symbols-sharp">error</span>Couldn't reach the API: {loadError}
        </div>
      </section>
    );
  }

  return (
    <section className="lane on">
      <h2>Log a Spot Nomination</h2>
      <p className="lede">
        Spot levels arrive by phone, email or broker rather than by letter,
        so they are entered by hand. The time you heard the quote is part of
        the record: a spot number without a timestamp cannot be placed
        against the market that day.
      </p>

      <div className="sec">
        <div className="card">
          <div className="form">
            <div className="f4">
              <label className="fl">Quoted at<span className="req">*</span></label>
              <div className="inline">
                <input type="datetime-local" className="ctl" value={when} onChange={(e) => setWhen(e.target.value)} />
                <button className="btn ghost" style={{ padding: "0 14px" }} onClick={() => setWhen(nowLocal())} title="Set to the current time">
                  <span className="material-symbols-sharp">schedule</span>Now
                </button>
              </div>
              <p className="hint">Local time. Backdate it if you are catching up on a call from earlier.</p>
            </div>

            <div className="f4">
              <label className="fl">Supplier<span className="req">*</span></label>
              <select className="ctl" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                <option value="">Select a supplier</option>
                {producers.map((p) => <option key={p.supplierId} value={p.supplierId}>{p.supplierName}</option>)}
                <option value="__other">Someone else, type the name</option>
              </select>
              {supplierId === "__other" && (
                <input
                  type="text" className="ctl" placeholder="Supplier name" style={{ marginTop: 10 }}
                  value={supplierOther} onChange={(e) => setSupplierOther(e.target.value)}
                />
              )}
            </div>

            <div className="f4">
              <label className="fl">How it came in</label>
              <select className="ctl" value={source} onChange={(e) => setSource(e.target.value)}>
                {sourceChannels.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f4">
              <label className="fl">Resin family<span className="req">*</span></label>
              <select className="ctl" value={family} onChange={(e) => { setFamily(e.target.value); setType(""); }}>
                <option value="">Select a family</option>
                {families.map((f) => <option key={f.resinFamilyCode} value={f.resinFamilyCode}>{f.displayName}</option>)}
              </select>
            </div>

            <div className="f4">
              <label className="fl">Resin type or grade</label>
              <select className="ctl" value={type} disabled={!family} onChange={(e) => setType(e.target.value)}>
                <option value="">{family ? "All grades in the family" : "Pick a family first"}</option>
                {selectedFamily?.types.map((t) => <option key={t.resinTypeCode} value={t.resinTypeCode}>{t.displayName}</option>)}
              </select>
            </div>

            <div className="f4">
              <label className="fl">Material condition</label>
              <select className="ctl" value={cond} onChange={(e) => setCond(e.target.value)}>
                {conditions.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f3">
              <label className="fl">Quoted level<span className="req">*</span></label>
              <div className="inline joined">
                <input type="number" className="ctl" step={0.25} min={0} placeholder="0.00" value={price} onChange={(e) => setPrice(e.target.value)} />
                <span className="suffix">¢/lb</span>
              </div>
            </div>

            <div className="f3">
              <label className="fl">Price basis</label>
              <select className="ctl" value={basis} onChange={(e) => setBasis(e.target.value)}>
                {priceBases.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f3">
              <label className="fl">Package</label>
              <select className="ctl" value={pack} onChange={(e) => setPack(e.target.value)}>
                {packages.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f3">
              <label className="fl">Region</label>
              <select className="ctl" value={region} onChange={(e) => setRegion(e.target.value)}>
                {regions.map((c) => <option key={c.code} value={c.code}>{c.displayName}</option>)}
              </select>
            </div>

            <div className="f5">
              <label className="fl">Volume offered</label>
              <div className="inline joined">
                <input type="number" className="ctl" step={1000} min={0} placeholder="e.g. 180000" value={qty} onChange={(e) => setQty(e.target.value)} />
                <span className="suffix">lb</span>
              </div>
              <p className="hint">Leave blank if the rep quoted a level without tying it to a quantity.</p>
            </div>

            <div className="f7">
              <label className="fl">Firmness</label>
              <div className="seg" role="group" aria-label="Firmness">
                {firmness.map((f) => (
                  <button key={f.code} type="button" aria-pressed={firm === f.code} onClick={() => setFirm(f.code)}>{f.displayName}</button>
                ))}
              </div>
              <p className="hint">Firm offers carry into the benchmark. Indications and hearsay are kept for context only.</p>
            </div>

            <div className="f12">
              <label className="fl">Context</label>
              <textarea
                className="ctl"
                placeholder="What was said, any conditions attached, how long the level holds, whether it undercuts a contract price you already have on file."
                value={notes} onChange={(e) => setNotes(e.target.value)}
              />
              <p className="hint">Free text stays out of the anonymization pipeline. It is visible to the RESIN8 team only.</p>
            </div>
          </div>

          <div className="formfoot">
            <span className="msg">
              {formMsg ?? (ready ? "Ready to log. The quote is stamped with the time above, not the time you press the button." : "Supplier, resin family, quoted level and time are needed before this can be logged.")}
            </span>
            <button className="btn ghost" onClick={resetForm}><span className="material-symbols-sharp">backspace</span>Clear Form</button>
            <button className="btn" disabled={!ready || submitting} onClick={logQuote}>
              <span className="material-symbols-sharp">bolt</span>{submitting ? "Logging…" : "Log Spot Quote"}
            </button>
          </div>
        </div>
      </div>

      {log.length > 0 && (
        <div className="sec">
          <div className="sec-hd">
            <h3>Your Logged Spot Quotes</h3>
            <span className="note">{log.filter((r) => r.firmnessCode === "FirmOffer").length} of {log.length} carry into the benchmark as firm offers.</span>
          </div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: "1%" }}>Quoted at</th><th>Supplier</th><th>Resin</th>
                  <th style={{ width: "1%" }}>Level</th><th>Basis</th><th>Region</th><th>Firmness</th><th>Context</th>
                </tr>
              </thead>
              <tbody>
                {log.map((r) => {
                  const fam = families.find((f) => f.resinFamilyCode === r.resinFamilyCode);
                  const typeName = r.resinTypeCode ? fam?.types.find((t) => t.resinTypeCode === r.resinTypeCode)?.displayName : null;
                  const firmMeta = firmness.find((f) => f.code === r.firmnessCode);
                  return (
                    <tr key={r.spotNominationId}>
                      <td style={{ whiteSpace: "nowrap", color: "var(--grey)" }}>{stampLabel(r.quotedAtLocal)}</td>
                      <td>{r.supplierName}</td>
                      <td>{typeName ? `${fam?.displayName} — ${typeName}` : `${fam?.displayName ?? r.resinFamilyCode}, all grades`}</td>
                      <td style={{ whiteSpace: "nowrap", color: "var(--lime)" }}>{r.priceCentsPerLb.toFixed(2)}¢</td>
                      <td>{priceBases.find((b) => b.code === r.priceBasisCode)?.displayName ?? r.priceBasisCode}
                        {r.volumeLb && <div style={{ color: "var(--grey)", fontSize: 11.5, marginTop: 3 }}>{Number(r.volumeLb).toLocaleString()} lb</div>}
                      </td>
                      <td>{regions.find((rg) => rg.code === r.regionCode)?.displayName ?? r.regionCode}</td>
                      <td>
                        {r.firmnessCode === "FirmOffer" ? <span className="chip ok">Firm</span> : r.firmnessCode === "Indication" ? <span className="chip info">Indication</span> : <span className="chip">{firmMeta?.displayName ?? "Heard"}</span>}
                      </td>
                      <td className="notecell">{r.notes || <span style={{ color: "#6e7568" }}>no context added</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
